import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  OnInit,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { Subject, of } from 'rxjs';
import { debounceTime, switchMap } from 'rxjs/operators';
import { DialogService } from '../../dialog/dialog.service';
import { ProductTypeService } from '../../product.type/product-type.service';
import { SettingService } from '../../setting/setting.service';
import { dayAsString, monthAsShortString } from '../../shared/date.helper';
import { Inspection } from '../../shared/interfaces';
import { permissions } from '../../shared/permissions';
import { UserService } from '../../user/user.service';
import { InspectionAddDateComponent } from '../add.date/add.date.component';
import { InspectionAssignVendorComponent } from '../assign.vendor/assign.vendor.component';
import { InspectionService } from '../inspection.service';
import { InspectionStatus } from '../inspection.status';
import { InspectionSendEmailComponent } from '../send.email/send.email.component';
import { AdminAssignmentInspectionComponent } from '../../shared/_components/admin-assignment/admin-assignmentcomponent';
import { SearchPipe } from './search.pipe';
import { InspectionAssigneeFilter } from '../inspection-assignee-filter/inspection-assignee-filter.component';

@Component({
  selector: 'app-inspection-flow',
  templateUrl: './inspection-flow.component.html',
  styleUrls: ['./inspection-flow.component.scss'],
  providers: [SearchPipe],
})
export class InspectionFlowComponent implements OnInit, AfterViewInit {
  constructor(
    public inspectionService: InspectionService,
    public userService: UserService,
    private toastr: ToastrService,
    private settingService: SettingService,
    private dialog: DialogService,
    private productTypeService: ProductTypeService,
    private router: Router,
    private route: ActivatedRoute,
    private cdr: ChangeDetectorRef,
    private searchPipe: SearchPipe,
  ) {}

  get orderedAssigned() {
    return [];
  }

  @ViewChild('assignVendorModal', { static: true }) public assignVendorModal: InspectionAssignVendorComponent;
  @ViewChild('addDateModal', { static: true }) public addDateModal: InspectionAddDateComponent;
  @ViewChild('sendEmailModal', { static: true }) public sendEmailModal: InspectionSendEmailComponent;
  @ViewChild('wrapper', { static: true }) private wrapper: ElementRef;
  @ViewChild('assignmentModal', { static: false }) assignmentModal!: AdminAssignmentInspectionComponent;
  @ViewChild('assignFilter', { static: false }) assignFilter!: InspectionAssigneeFilter;

  noDate: Inspection[] = null;
  toBeAssigned: Inspection[] = null;
  assigned: Inspection[] = null;
  needsReview: Inspection[] = null;
  underReview: Inspection[] = null;
  dueCompletion: Inspection[] = null;
  filteredAssignees: number[] = [];
  loading = false;
  search = '';

  productTypes = [];

  loadDebounced = new Subject();
  assignedTimeFrame: number;
  needsReviewHighlightTimeFrame: number;
  needsReviewHideTimeframe: number;
  selectedType: string;
  canAdd = false;
  canEdit = false;
  canReview = false;
  canRemind = false;
  canExport = false;

  scoreThresholdAmber;
  scoreThresholdOrange;
  scoreThresholdRed;
  filters = {};
  scoresEnabled = true;
  activeNeedsReviewSort = 'HighestScoredFirst';
  activeUnderReviewSort = 'HighestScoredFirst';
  filteredItems: any[] = [];
  selectedProductTypes = [];
  ngAfterViewInit() {
    // wait a bit for dom to render
    setTimeout(() => {
      this.setWrapperHeight();
    }, 500);
  }

  // Set component to use 100% of available height
  setWrapperHeight() {
    this.wrapper.nativeElement.style.height =
      window.innerHeight - this.wrapper.nativeElement.getBoundingClientRect().top + 'px';
  }

  // Re-calculate available height
  @HostListener('window:resize')
  handleResize() {
    this.setWrapperHeight();
  }

  async ngOnInit() {
    this.loading = true;
    // Debounce data loading
    this.loadDebounced
      .pipe(
        debounceTime(500),
        switchMap(() => of(this.loadDataLive())),
      )
      .subscribe();

    // await the parallel requests
    [
      this.assignedTimeFrame,
      this.needsReviewHideTimeframe,
      this.needsReviewHighlightTimeFrame,
      this.scoresEnabled,
      this.scoreThresholdAmber,
      this.scoreThresholdOrange,
      this.scoreThresholdRed,
      this.productTypes,
      this.filters,
    ] = await Promise.all([
      this.settingService.assignedTimeFrame(),
      this.settingService.needsReviewHideTimeframe(),
      this.settingService.needsReviewHighlightTimeFrame(),
      this.settingService.scoresEnabled(),
      this.settingService.scoreThresholdAmber(),
      this.settingService.scoreThresholdOrange(),
      this.settingService.scoreThresholdRed(),
      this.productTypeService.list().then(res => res[0].map(x => ({ ...x, checked: true }))),
      (this.filters = await this.userService.getSetting('flowFilters')),
    ]);

    this.applyPreference();
    await this.loadDataLive();

    this.canAdd = await this.userService.hasPermissions([permissions.INSPECTION.CREATE]);
    this.canEdit = await this.userService.hasPermissions([permissions.INSPECTION.EDIT]);
    this.canReview = await this.userService.hasPermissions([permissions.INSPECTION.REVIEW]);
    this.canRemind = await this.userService.hasPermissions([permissions.INSPECTION.SEND_REMINDERS]);
    this.canExport = await this.userService.hasPermissions([permissions.INSPECTION.EXPORT]);
    this.loading = false;
  }

  loadData() {
    this.loadDebounced.next();
  }

  async loadDataLive() {
    this.loading = true;
    const inspections = await this.inspectionService.flow();

    this.noDate = inspections[InspectionStatus.NO_DATE_ASSIGNED].map(this.mapInspection);

    const toBeAssignedThreshold = new Date();
    toBeAssignedThreshold.setDate(toBeAssignedThreshold.getDate() + this.assignedTimeFrame);

    const toBeAssigned = inspections[InspectionStatus.READY_TO_BE_ASSIGNED];

    const assignmentMade = inspections[InspectionStatus.ASSIGNMENT_MADE].filter(
      insp => new Date(insp.dueDate) && new Date(insp.dueDate) < toBeAssignedThreshold,
    );

    this.toBeAssigned = toBeAssigned.filter(i => !i.vendor).map(this.mapInspection);

    const weekAgo = new Date();
    weekAgo.setDate(weekAgo.getDate() - 7);

    const nextWeek = new Date();
    nextWeek.setDate(nextWeek.getDate() + 7);

    const allAssigned = [...assignmentMade, ...toBeAssigned.filter(i => i.vendor)];

    this.assigned = allAssigned
      .map(this.mapInspection)
      .filter(i => !(i.overdue && i.inspector))
      .map(i => ({
        ...i,
        // "both overdue as well as cards that are due within the next 7 days and have no inspector assigned"
        overdue:
          i.overdue || (!i.inspector && i.dueDate && new Date(i.dueDate).getTime() < nextWeek.getTime() ? true : false),
      }))
      .sort((a, b) => (new Date(a.dueDate) > new Date(b.dueDate) ? 1 : -1));

    this.dueCompletion = allAssigned
      .map(this.mapInspection)
      .filter(i => i.overdue && i.inspector)
      .map(i => ({
        ...i,
        overdue: i.dueDate && new Date(i.dueDate).getTime() < weekAgo.getTime() ? true : false,
      }));

    // Oldest first
    const needsReview = inspections[InspectionStatus.NEEDS_REVIEW]
      .map(this.mapInspection)
      .sort((a, b) =>
        new Date(a.rejected ? a.resubmitted : a.completed) > new Date(b.rejected ? b.resubmitted : b.completed)
          ? 1
          : -1,
      );

    // Oldest first
    // Use "resubmitted" date it it's rejected
    const invalidSync = inspections[InspectionStatus.INVALID_SYNC]
      .map(this.mapInspection)
      .map(x => ({ ...x, invalid: true }));

    // Invalids -> resubmitted -> others
    this.needsReview = [
      ...invalidSync,
      // If flag scores are enabled, show scored inspections first
      ...(this.scoresEnabled ? needsReview.filter(i => i.flaggingScore).map(x => ({ ...x, invalid: false })) : []),
      ...needsReview
        .filter(i => i.resubmitted && (!this.scoresEnabled || !i.flaggingScore))
        .map(x => ({ ...x, invalid: false })),
      ...needsReview
        .filter(i => !i.resubmitted && (!this.scoresEnabled || !i.flaggingScore))
        .map(x => ({ ...x, invalid: false })),
    ]
      .filter((i: any) => !i.under_review)
      .map(i => ({
        ...i,
        daysSince: this.daysSince(i.completed),
      }))
      .filter(i => {
        if (this.needsReviewHideTimeframe) {
          if (i.daysSince < this.needsReviewHideTimeframe) {
            return i;
          }
        } else {
          return i;
        }
      });

    // Resubmitted -> others
    this.underReview = [
      // If flag scores are enabled, show scored inspections first
      ...(this.scoresEnabled
        ? needsReview.filter(i => i.flaggingScore).sort((a, b) => b.flaggingScore - a.flaggingScore)
        : []),
      ...needsReview.filter(i => i.resubmitted && (!this.scoresEnabled || !i.flaggingScore)),
      ...needsReview.filter(i => !i.resubmitted && (!this.scoresEnabled || !i.flaggingScore)),
    ]
      .filter((i: any) => i.under_review)
      .map(i => ({
        ...i,
        daysSince: this.daysSince(i.completed),
      }))
      .filter(i => {
        if (this.needsReviewHideTimeframe) {
          if (i.daysSince < this.needsReviewHideTimeframe) {
            return i;
          }
        } else {
          return i;
        }
      });

    const needsReviewSortPreference = this.getSortPreference('needsReview');
    switch (needsReviewSortPreference) {
      case 'HighestScoredFirst':
        this.sortNeedsReviewHighestScoredFirst();
        break;
      case 'OldestFirst':
        this.sortNeedsReviewOldestFirst();
        break;
      case 'NewestFirst':
        this.sortNeedsReviewNewestFirst();
        break;
      default:
        this.sortNeedsReviewHighestScoredFirst();
    }

    const underReviewSortPreference = this.getSortPreference('underReview');
    switch (underReviewSortPreference) {
      case 'HighestScoredFirst':
        this.sortUnderReviewHighestScoredFirst();
        break;
      case 'OldestFirst':
        this.sortUnderReviewOldestFirst();
        break;
      case 'NewestFirst':
        this.sortUnderReviewNewestFirst();
        break;
      default:
        this.sortUnderReviewHighestScoredFirst();
    }

    this.activeNeedsReviewSort = needsReviewSortPreference;
    this.activeUnderReviewSort = underReviewSortPreference;
    this.loading = false;
  }

  applyPreference() {
    if (this.filters && this.filters['filteredAssignees']) {
      this.filteredAssignees = this.filters['filteredAssignees'];
    }

    this.productTypes = this.productTypes.map(item => {
      if (
        this.filters &&
        this.filters['selectedProductTypes'] &&
        !this.filters['selectedProductTypes'].includes(item.id)
      ) {
        item.checked = false;
      }
      return item;
    });
  }

  sortNeedsReviewHighestScoredFirst() {
    this.needsReview.sort((a: any, b: any) => {
      if (a.invalid !== b.invalid) {
        return b.invalid ? 1 : -1;
      }

      // First sort
      if (a.flaggingScore && b.flaggingScore) {
        return b.flaggingScore - a.flaggingScore;
      } else if (a.flaggingScore) {
        return -1;
      } else if (b.flaggingScore) {
        return 1;
      }

      // Second sort - the oldest first
      const daysSinceA = this.daysSince(new Date(a.completed));
      const daysSinceB = this.daysSince(new Date(b.completed));

      if (daysSinceA === daysSinceB) {
        return 0;
      }
      return daysSinceB - daysSinceA;
    });
    this.saveSortPreference('HighestScoredFirst', 'needsReview');
    this.activeNeedsReviewSort = 'HighestScoredFirst';
  }

  sortNeedsReviewOldestFirst() {
    this.needsReview.sort((a: any, b: any) => {
      if (a.invalid !== b.invalid) {
        return b.invalid ? 1 : -1;
      }

      const daysSinceA = this.daysSince(new Date(a.completed));
      const daysSinceB = this.daysSince(new Date(b.completed));

      // Second sort - the highest score
      if (daysSinceA === daysSinceB) {
        return (b.flaggingScore || 0) - (a.flaggingScore || 0);
      }
      return daysSinceB - daysSinceA;
    });
    this.saveSortPreference('OldestFirst', 'needsReview');
    this.activeNeedsReviewSort = 'OldestFirst';
  }

  sortNeedsReviewNewestFirst() {
    this.needsReview.sort((a: any, b: any) => {
      if (a.invalid !== b.invalid) {
        return b.invalid ? 1 : -1;
      }

      const daysSinceA = this.daysSince(new Date(a.completed));
      const daysSinceB = this.daysSince(new Date(b.completed));

      // Second sort - the highest score
      if (daysSinceA === daysSinceB) {
        return (b.flaggingScore || 0) - (a.flaggingScore || 0);
      }
      return daysSinceA - daysSinceB;
    });

    this.saveSortPreference('NewestFirst', 'needsReview');
    this.activeNeedsReviewSort = 'NewestFirst';
  }

  sortUnderReviewHighestScoredFirst() {
    this.underReview.sort((a, b) => {
      // First sort
      if (a.flaggingScore && b.flaggingScore) {
        return b.flaggingScore - a.flaggingScore;
      } else if (a.flaggingScore) {
        return -1;
      } else if (b.flaggingScore) {
        return 1;
      }

      // Second sort - the oldest first
      const daysSinceA = this.daysSince(new Date(a.completed));
      const daysSinceB = this.daysSince(new Date(b.completed));

      return daysSinceB - daysSinceA;
    });
    this.saveSortPreference('HighestScoredFirst', 'underReview');
    this.activeUnderReviewSort = 'HighestScoredFirst';
  }

  sortUnderReviewOldestFirst() {
    this.underReview.sort((a, b) => {
      const daysSinceA = this.daysSince(new Date(a.completed));
      const daysSinceB = this.daysSince(new Date(b.completed));

      // Second sort - the highest score
      if (daysSinceA === daysSinceB) {
        return (b.flaggingScore || 0) - (a.flaggingScore || 0);
      }
      return daysSinceB - daysSinceA;
    });
    this.saveSortPreference('OldestFirst', 'underReview');
    this.activeUnderReviewSort = 'OldestFirst';
  }

  sortUnderReviewNewestFirst() {
    this.underReview.sort((a, b) => {
      const daysSinceA = this.daysSince(new Date(a.completed));
      const daysSinceB = this.daysSince(new Date(b.completed));

      // Second sort - the highest score
      if (daysSinceA === daysSinceB) {
        return (b.flaggingScore || 0) - (a.flaggingScore || 0);
      }
      return daysSinceA - daysSinceB;
    });
    this.saveSortPreference('NewestFirst', 'underReview');
    this.activeUnderReviewSort = 'NewestFirst';
  }

  saveSortPreference(pref, category) {
    if (!this.filters) {
      this.filters = {};
    }
    this.filters[category] = pref;
    this.savePreference();
  }

  getSortPreference(category) {
    return this.filters?.[category];
  }

  mapInspection(insp: Inspection) {
    return {
      ...insp,
      day: insp.dueDate ? dayAsString(new Date(insp.dueDate)) : '',
      month: insp.dueDate ? monthAsShortString(new Date(insp.dueDate)) : '',
      overdue: insp.dueDate && new Date(insp.dueDate).getTime() < new Date().getTime() ? true : false,
    };
  }

  getParams() {
    return {
      orderBy: 'inspection.dueDate',
      pageSize: 9999,
      asc: true,
    };
  }

  assignVendor(ev, inspection: Inspection) {
    ev.stopPropagation();
    this.assignVendorModal.show({
      inspection,
      confirmed: () => {
        this.toastr.success('Inspection saved!');
        this.loadData();
      },
    });
  }

  addDate(ev, inspection: Inspection) {
    ev.stopPropagation();
    this.addDateModal.show({
      inspection,
      confirmed: () => {
        this.toastr.success('Inspection saved!');
        this.loadData();
      },
    });
  }

  sendReminderAssign(ev, inspection: Inspection) {
    ev.stopPropagation();
    this.dialog.confirm().show({
      message: 'Send reminder to vendor?',
      confirmed: async () => {
        await this.inspectionService.sendReminderVendorAssign(inspection.id);
        this.toastr.success('Message sent!');
      },
    });
  }

  daysSince(_date) {
    const date = new Date(_date);
    return Math.round((Date.now() - date.getTime()) / 1000 / 60 / 60 / 24);
  }

  downloadSummary() {
    this.inspectionService.downloadSummaryCsv();
  }

  getInspectionScoreClass(inspection) {
    if (!this.scoresEnabled) {
      return {};
    }

    return {
      'score-amber':
        inspection.flaggingScore >= this.scoreThresholdAmber && inspection.flaggingScore < this.scoreThresholdOrange,
      'score-orange':
        inspection.flaggingScore >= this.scoreThresholdOrange && inspection.flaggingScore < this.scoreThresholdRed,
      'score-red': inspection.flaggingScore >= this.scoreThresholdRed,
      'has-score': this.scoresEnabled && inspection.flaggingScore,
    };
  }

  getDaysClass(inspection) {
    return {
      red: inspection.daysSince > this.needsReviewHighlightTimeFrame,
    };
  }

  setSearchTerm(search) {
    this.search = search;
  }

  shouldShowEmailModal(inspection) {
    return this.canRemind && this.sendEmailModal.availableEmails(inspection).length > 0;
  }

  showEmailModal(inspection) {
    this.sendEmailModal.show({ inspection });
  }

  filterProductTypes(selected: any[]) {
    if (!selected || selected.length === 0) {
      this.filteredItems = [];
      return;
    }
    this.filters['selectedProductTypes'] = selected.map(item => item.id);
    this.savePreference();
  }

  updateAssignees(assigneeIds: number[]) {
    this.filters['filteredAssignees'] = assigneeIds;
    this.filteredAssignees = assigneeIds;
    this.savePreference();
  }

  getInitials(name: string): string {
    const words = name.trim().split(/\s+/);
    return words.length >= 2 ? words[0][0] + words[1][0] : words[0][0];
  }

  async openAssignmentModal(inspection, type): Promise<void> {
    this.selectedType = type;
    const canAssign = await this.userService.hasPermissions([permissions.INSPECTION.CAN_ASSIGN]);
    if (canAssign) {
      this.assignmentModal.openModal(inspection);
    }
  }

  handleConfirm(data): void {
    const inspectionMapping = {
      noDate: this.noDate,
      toBeAssigned: this.toBeAssigned,
      assigned: this.assigned,
      needsReview: this.needsReview,
      underReview: this.underReview,
      dueCompletion: this.dueCompletion,
    };
    const inspections = inspectionMapping[this.selectedType];

    if (inspections) {
      const inspection = inspections.find(ins => ins.id === data.inspectionId);
      if (inspection) {
        inspection.assignee = data.admin ? data.admin : null;
        if (data.admin) {
          inspection.assigneeId = data?.admin.id;
        } else {
          inspection.assigneeId = null;
        }
      }
      this.cdr.markForCheck();
    }
  }

  checkFilteredData(inspections: any[], searchTerm: string, productTypes: any[], filteredAssignees: number[]): boolean {
    return (
      inspections?.length &&
      this.searchPipe.transform(inspections, { searchTerm, productTypes, filteredAssignees }).length > 0
    );
  }

  isNoDate() {
    return !this.checkFilteredData(this.noDate, this.search, this.productTypes, this.filteredAssignees);
  }
  isToBeAssignedData() {
    return !this.checkFilteredData(this.toBeAssigned, this.search, this.productTypes, this.filteredAssignees);
  }
  isAssignedData() {
    return !this.checkFilteredData(this.assigned, this.search, this.productTypes, this.filteredAssignees);
  }
  isDueCompletion() {
    return !this.checkFilteredData(this.dueCompletion, this.search, this.productTypes, this.filteredAssignees);
  }
  isNeedsReview() {
    return !this.checkFilteredData(this.needsReview, this.search, this.productTypes, this.filteredAssignees);
  }

  async reset() {
    this.search = '';
    this.loadDataLive();
    this.filteredAssignees = [];
    this.productTypes = await this.productTypeService.list().then(res => res[0].map(x => ({ ...x, checked: true })));
    this.assignFilter.reset();
    this.router.navigate(['/'], {
      queryParams: { search: '' },
      queryParamsHandling: 'merge',
    });
    this.cdr.detectChanges();
  }

  savePreference() {
    this.userService.saveSetting('flowFilters', this.filters).subscribe();
  }
}
